/**
 * Authentication Service
 * Handles JWT token management, storage, and decoding
 */

import { STORAGE_KEYS } from '@/config/appConfig';

/**
 * JWT Payload structure from backend
 */
export interface JWTPayload {
  sub: string; // userId GUID
  email: string;
  name: string;
  role?: string; // Role name, e.g. "Admin" — present on every token the API issues
  department?: string;
  azureId?: string;
  subdomain?: string;
  tenantId: string;
  employeeCode?: string; // Employee code for display
  exp: number; // Unix timestamp
  iat?: number; // Issued at
  iss?: string; // Issuer
  aud?: string; // Audience
}

/**
 * User payload extracted from JWT
 */
export interface UserPayload {
  userId: string;
  email: string;
  name: string;
  role?: string;
  department?: string;
  azureId?: string;
  subdomain?: string;
  tenantId: string;
  expiresAt: Date;
  employeeCode: string;
  photoUrl?: string; // Added photoUrl for better handling of profile photos
}

/**
 * Session keys that used to be written to localStorage, before the session was
 * scoped to the tab. Kept only so an existing install can be cleaned up.
 */
const LEGACY_LOCAL_STORAGE_KEYS = [
  STORAGE_KEYS.token,
  STORAGE_KEYS.user,
  STORAGE_KEYS.attendance,
  'probation_notice_shown',
];

/**
 * Removes session data left in localStorage by an earlier version of the app.
 *
 * Sessions now live in sessionStorage and end with the tab. Anyone who was signed
 * in before this change still has a token sitting in localStorage, where it would
 * survive indefinitely and never be read again — this clears it on next load.
 * Device preferences (theme, timezone, push flags) are not touched.
 */
export function purgeLegacySessionKeys(): void {
  try {
    LEGACY_LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.error('Failed to clear legacy session keys:', error);
  }
}

/**
 * The access token lives in a module variable and nowhere else — not localStorage, not
 * sessionStorage, not a readable cookie. Anything reachable from JS is reachable from an
 * XSS payload; a variable dies with the page.
 *
 * The cost is that a reload starts with no token. That is what the /auth/refresh call on
 * app boot is for: the HttpOnly refresh cookie survives the reload and buys a new one.
 */
let accessToken: string | null = null;

/**
 * Get the in-memory access token for this page.
 */
export function getToken(): string | null {
  return accessToken;
}

/**
 * Save the access token for this page.
 */
export function setToken(token: string): void {
  accessToken = token;
}

/**
 * Remove the token and everything else tied to the session.
 *
 * Note this cannot clear the refresh cookie — it is HttpOnly by design. Call
 * POST /auth/logout to have the server revoke the family and expire it.
 */
export function removeToken(): void {
  accessToken = null;
  sessionStorage.removeItem(STORAGE_KEYS.token);
  sessionStorage.removeItem(STORAGE_KEYS.user);
  sessionStorage.removeItem(STORAGE_KEYS.attendance);
}

/**
 * Decode JWT token payload (client-side, no verification)
 * Returns null if token is invalid or malformed
 * Handles both standard JWT claims and C# ClaimTypes (URI-based claims)
 */
export function decodeToken(token: string): JWTPayload | null {
  try {
    // JWT structure: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode base64url payload
    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    const rawPayload = JSON.parse(jsonPayload);

    // C# ClaimTypes are written either as their full URI or as the short JWT name,
    // depending on whether the server clears DefaultOutboundClaimTypeMap. Probe both —
    // the API does the same in reverse (UserContextMiddleware).
    const claimNameIdentifier = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier';
    const claimEmailAddress = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress';
    const claimName = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name';
    const claimRole = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

    // A token with several roles carries the claim as an array; the app models one role.
    const rawRole = rawPayload[claimRole] ?? rawPayload.role;
    const role = Array.isArray(rawRole) ? rawRole[0] : rawRole;

    // Normalize to standard JWT claims
    return {
      sub: rawPayload[claimNameIdentifier] || rawPayload.sub || rawPayload.nameid,
      email: rawPayload[claimEmailAddress] || rawPayload.email,
      name: rawPayload[claimName] || rawPayload.name || rawPayload.unique_name,
      role: role || undefined,
      department: rawPayload.department,
      azureId: rawPayload.azureId,
      subdomain: rawPayload.subdomain,
      tenantId: rawPayload.tenantId,
      employeeCode: rawPayload.employeeCode,
      exp: rawPayload.exp,
      iat: rawPayload.iat,
      iss: rawPayload.iss,
      aud: rawPayload.aud,
    } as JWTPayload;
  } catch (error) {
    console.error('Failed to decode JWT token:', error);
    return null;
  }
}

/**
 * Check if JWT token is expired
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) {
    return true;
  }

  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  
  return currentTime >= expirationTime;
}

/**
 * Check if user is authenticated (has valid, non-expired token)
 */
export function isAuthenticated(): boolean {
  const token = getToken();
  if (!token) {
    return false;
  }

  // Check if token is expired
  if (isTokenExpired(token)) {
    removeToken(); // Clean up expired token
    return false;
  }

  return true;
}

/**
 * True when a well-formed, unexpired token is present.
 *
 * Unlike isAuthenticated() this is side-effect free, so it is safe to call
 * during render (route guards) — clearing the expired session is left to the
 * caller that owns auth state.
 */
export function hasValidToken(): boolean {
  const token = getToken();
  return !!token && !isTokenExpired(token);
}

/**
 * Get user information from JWT token
 * Returns null if token is invalid or expired
 */
export function getUserFromToken(): UserPayload | null {
  const token = getToken();
  if (!token) {
    return null;
  }

  if (isTokenExpired(token)) {
    removeToken();
    return null;
  }

  const payload = decodeToken(token);
  if (!payload) {
    return null;
  }

  return {
    userId: payload.sub,
    email: payload.email,
    name: payload.name,
    role: payload.role,
    department: payload.department,
    azureId: payload.azureId,
    subdomain: payload.subdomain,
    tenantId: payload.tenantId,
    employeeCode: payload.employeeCode || 'N/A',
    expiresAt: new Date(payload.exp * 1000),
  };
}

/**
 * Get user ID from token
 */
export function getUserId(): string | null {
  const user = getUserFromToken();
  return user?.userId || null;
}

/**
 * Get tenant ID from token
 */
export function getTenantId(): string | null {
  const user = getUserFromToken();
  return user?.tenantId || null;
}

/**
 * Logout user by clearing all auth data
 */
export function logout(): void {
  removeToken();
  window.location.href = '/login';
}

/**
 * Check if token will expire soon (within 5 minutes)
 * Useful for implementing token refresh logic
 */
export function isTokenExpiringSoon(): boolean {
  const token = getToken();
  if (!token) {
    return false;
  }

  const payload = decodeToken(token);
  if (!payload || !payload.exp) {
    return false;
  }

  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  const fiveMinutes = 5 * 60 * 1000;

  return expirationTime - currentTime < fiveMinutes;
}
